import { HoverableLinkSizes } from '@paywithextend/components'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React, { ComponentProps, useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DeactivatedUserIndicator from 'src/components/DeactivatedUserIndicator/DeactivatedUserIndicator'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardPermissions,
  CreditCardPermissionsModel,
} from 'src/models/creditCardPermissions.models'
import {
  TransactionDetailModel,
  TransactionSource,
} from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import {
  accountOverviewUrl,
  budgetOverviewUrl,
  peopleMembersDetailUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { getReceiptRules } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'

import style from './CardDetailsSection.style'

interface CardDetailsSectionProps {
  transaction: TransactionDetailModel
  creditCardPermissions?: CreditCardPermissionsModel
  parentCreditCardPermissions?: CreditCardPermissionsModel
  cardUser?: UserListModel
  cardCreator?: UserListModel
}

const DetailContent: React.FC<
  {
    title: string
    tooltip?: React.ReactElement
    to?: string
    toAriaLabel?: string
    analyticsOnClick?: () => void
  } & ComponentProps<'div'>
> = ({ title, tooltip, to, toAriaLabel, analyticsOnClick, children }) => {
  let content
  if (to && toAriaLabel) {
    content = (
      <HoverableLink
        className={'detail-info link'}
        to={to}
        size={HoverableLinkSizes.Large}
        underline
        ariaLabel={toAriaLabel}
        onClick={analyticsOnClick}
        regular
      >
        {children}
      </HoverableLink>
    )
  } else {
    content = <div className={'detail-info'}>{children}</div>
  }

  return (
    <div className={'detail'}>
      <div className={'detail-header'}>
        {title}
        {tooltip}
      </div>
      {content}
    </div>
  )
}

const CardDetailsSection: React.FC<CardDetailsSectionProps> = ({
  transaction,
  creditCardPermissions,
  parentCreditCardPermissions,
  cardUser,
  cardCreator,
}): React.ReactElement => {
  const translate = useTranslate()
  const orgReceiptRulesEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest

  const { analytics } = useAnalyticsContext()

  const canReadCreditCard = creditCardPermissions?.canRead
  const canReadParentCreditCard = parentCreditCardPermissions?.canRead

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    if (
      organization?.id !== undefined &&
      organization?.receiptRule === undefined &&
      orgReceiptRulesEnabled
    ) {
      dispatch(getReceiptRules(organization.id))
    }
  }, [organization?.id, orgReceiptRulesEnabled])

  // If the card does not have IssueDelegateCreditCards - it is a budget
  const isBudget = !creditCardPermissions?.permissions.includes(
    CreditCardPermissions.IssueDelegateCreditCards,
  )

  const isPhysicalTransaction = [
    TransactionSource.Physical,
    TransactionSource.Other,
  ].includes(transaction.source)

  return (
    <div css={style} className={'cardDetailsSection'}>
      <div className={'cardDetailsSection title'}>
        {translate('Card Details')}
      </div>
      <div className={'cardDetailsSection content'}>
        <DetailContent
          title={translate('Card')}
          // If no virtualCardId, then it is a physical card transaction so use creditCardId
          to={
            transaction.virtualCardId
              ? virtualCardDetailUrl(transaction.virtualCardId)
              : accountOverviewUrl(transaction.creditCardId)
          }
          toAriaLabel={transaction.vcnDisplayName}
        >
          {transaction.vcnDisplayName}
        </DetailContent>
        <DetailContent
          title={translate('Card User')}
          to={peopleMembersDetailUrl(transaction.recipientId)}
          toAriaLabel={transaction.recipientName}
          analyticsOnClick={() =>
            analytics?.track(
              'transaction detail card details - open user detail - text press',
              {
                screen: `/transactions/${transaction.recipientId}`,
                interaction: 'text press',
                intent: 'open user detail',
              },
            )
          }
        >
          {transaction.recipientName}
          {cardUser?.deactivated && <DeactivatedUserIndicator />}
        </DetailContent>
        {!isPhysicalTransaction && canReadCreditCard && (
          <DetailContent
            title={translate('Created with')}
            to={
              transaction.creditCardId !== undefined
                ? isBudget
                  ? budgetOverviewUrl(transaction.creditCardId)
                  : accountOverviewUrl(transaction.creditCardId)
                : undefined
            }
            toAriaLabel={transaction.creditCardDisplayName}
          >
            {transaction.creditCardDisplayName}
          </DetailContent>
        )}
        {!isPhysicalTransaction && transaction.cardholderName !== undefined && (
          <DetailContent
            title={translate('Card Creator')}
            to={
              !isGuestUser && cardCreator?.id !== undefined
                ? peopleMembersDetailUrl(cardCreator?.id)
                : undefined
            }
            toAriaLabel={transaction.cardholderName}
            analyticsOnClick={() =>
              analytics?.track(
                'transaction detail card details - open user detail - text press',
                {
                  screen: `/transactions/${cardCreator?.id}`,
                  interaction: 'text press',
                  intent: 'open user detail',
                },
              )
            }
          >
            {transaction.cardholderName}
            {cardCreator?.deactivated && <DeactivatedUserIndicator />}
          </DetailContent>
        )}
        {canReadParentCreditCard &&
          transaction.parentCreditCardId !== undefined && (
            <DetailContent
              title={translate('Parent Account')}
              to={accountOverviewUrl(transaction.parentCreditCardId)}
              toAriaLabel={transaction.parentCreditCardDisplayName}
            >
              {transaction.parentCreditCardDisplayName}
            </DetailContent>
          )}
        <DetailContent
          title={translate('Origin')}
          tooltip={
            <TooltipIcon>
              <div className={'flex-column gap-3'}>
                <p>
                  {translate(
                    'Virtual indicates this transaction was made on a Virtual Card.',
                  )}
                </p>
                <p>
                  {translate(
                    'Physical or Other indicates this transaction was made with a physical card.',
                  )}
                </p>
              </div>
            </TooltipIcon>
          }
        >
          {transaction.sourceLabel}
        </DetailContent>
      </div>
    </div>
  )
}

export default CardDetailsSection
